import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { StatusProvider } from "./context/StatusContext";
import MainLayout from "./components/layouts/MainLayout";
import { ADMIN_ROLES, ROLE_VALUES } from "./utils/roles";


// pages
import Home from "./pages/Home";
import Projects from "./pages/Projects";
import DeletedProjects from "./pages/DeletedProjects";
import Tasks from "./pages/Tasks";
import DeletedTasks from "./pages/DeletedTasks";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import DeletedClients from "./pages/DeletedClients";
import Contact from "./pages/Contact";
import Reports from "./pages/Reports";
import DeletedReports from "./pages/DeletedReports";
import Payroll from "./pages/Payroll";
import EmployeePayrollProfile from "./pages/EmployeePayrollProfile";
import DeletedPayrolls from "./pages/DeletedPayrolls";
import Users from "./pages/Users";
import CreateUser from "./pages/CreateUser";
import Settings from "./pages/Settings";
import ChangePassword from "./pages/ChangePassword";
import Login from "./pages/Login";
import TeamOverview from "./pages/TeamOverview";
import Careers from "./pages/Careers";
import CareersAdmin from "./pages/CareersAdmin";
import CareersApplications from "./pages/CareersApplications";
import Brief from "./pages/Brief";
import BriefDetail from "./pages/BriefDetail";
import ReportDaily from "./pages/ReportDaily";
import MonthlyReport from "./pages/Monthlyreport";
import Expense from "./pages/Expense";


const Messages = lazy(() => import("./pages/Messages"));

// ✅ حذف UnreadMessagesContext و UnreadMessagesProvider القديمين —
// العداد والإشعارات بقوا كلهم في AuthContext (unreadCounts, totalUnreadCount)

function AppContent() {
  const { currentUser, totalUnreadCount } = useAuth();
  // ✅ مفيش أي كود socket هنا — AuthContext بيتكلم عليه كله

  return (
    <BrowserRouter>
      <Helmet>
        <title>{totalUnreadCount > 0 ? `(${totalUnreadCount > 99 ? '99+' : totalUnreadCount}) Growth Station` : 'Growth Station'}</title>
      </Helmet>
    
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0b1220",
            color: "#fff",
            border: "1px solid #1f2937",
            borderRadius: "12px",
            padding: "14px 16px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            zIndex: 10000,
          },
          success: {
            style: {
              background: "#22c55e",
              border: "1px solid #22c55e",
            },
          },
          error: {
            style: {
              background: "#ef4444",
              border: "1px solid #ef4444",
            },
          },
        }}
      />
      <Suspense fallback={<div style={{ padding: 16 }}>Loading…</div>}>
        <Routes>
          {/* PUBLIC */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<CreateUser />} />
          <Route path="/careers" element={<Careers />} />

          {/* CHANGE PASSWORD */}
          <Route
            path="/change-password"
            element={
              currentUser?.mustChangePassword ? (
                <ChangePassword />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* PROTECTED */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/deleted" element={<DeletedProjects />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/tasks/deleted" element={<DeletedTasks />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/daily-report" element={<ReportDaily />} />

              <Route element={<ProtectedRoute allowedRoles={ADMIN_ROLES} />}>
                <Route path="/overview" element={<TeamOverview />} />
                <Route path="/careers-admin" element={<CareersAdmin />} />
                <Route path="/careers-applications" element={<CareersApplications />} />
              </Route>

              <Route element={<ProtectedRoute allowedRoles={ADMIN_ROLES} />}>
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/deleted" element={<DeletedClients />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/Contact" element={<Contact />} />
                <Route path="/users" element={<Users />} />
                <Route path="/create-user" element={<CreateUser />} />
                <Route path="/brief" element={<Brief />} />
                <Route path="/brief/:id" element={<BriefDetail />} />
              </Route>

              <Route path="/reports" element={<Reports />} />
              <Route path="/reports/deleted" element={<DeletedReports />} />

              <Route element={<ProtectedRoute allowedRoles={[ROLE_VALUES.FOUNDER]} />}>
                <Route path="/finance" element={<Payroll />} />
                <Route path="/finance/payroll/:empId" element={<EmployeePayrollProfile />} />
                <Route path="/finance/deleted" element={<DeletedPayrolls />} />
                <Route path="/finance/expenses" element={<Expense />} />
                <Route path="/finance/monthly-report" element={<MonthlyReport />} />
              </Route>
            </Route>
          </Route>

          {/* FALLBACK */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function App() {
  return (
    // ✅ StatusProvider بس — AuthProvider موجود في index.jsx أو main.jsx فوق ده
    <HelmetProvider>
      <StatusProvider>
        <AppContent />
      </StatusProvider>
    </HelmetProvider>
  );
}

export default App;